import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import mutate from "@/Utils/request/mutate";
import {
  PatientPreparation,
  ServiceRequestReadSpec,
  toServiceRequestUpdateSpec,
} from "@/types/emr/serviceRequest/serviceRequest";
import serviceRequestApi from "@/types/emr/serviceRequest/serviceRequestApi";

const FASTING_STATUS_OPTIONS = [
  { value: "not_fasting", label: "Not fasting" },
  { value: "fasting_2_4h", label: "Fasting (2–4 hrs)" },
  { value: "fasting_4_8h", label: "Fasting (4–8 hrs)" },
  { value: "fasting_8h_plus", label: "Fasting (8+ hrs)" },
];

const DIET_RESTRICTION_OPTIONS = [
  { value: "no_restrictions", label: "No restrictions" },
  { value: "avoid_fatty_foods", label: "Avoid fatty foods" },
  { value: "low_sodium", label: "Low-sodium diet" },
  { value: "npo", label: "NPO (nothing by mouth)" },
];

const BLADDER_STATUS_OPTIONS = [
  { value: "empty", label: "Empty" },
  { value: "full", label: "Full (hold urine)" },
  { value: "partially_full", label: "Partially full" },
  { value: "not_applicable", label: "Not applicable" },
];

interface PatientPreparationSectionProps {
  request: ServiceRequestReadSpec;
  facilityId: string;
  serviceRequestId: string;
  disableEdit: boolean;
}

export function PatientPreparationSection({
  request,
  facilityId,
  serviceRequestId,
  disableEdit,
}: PatientPreparationSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [preparation, setPreparation] = useState<PatientPreparation>(
    request.patient_preparation ?? {},
  );

  // Sync with request data changes
  useEffect(() => {
    setPreparation(request.patient_preparation ?? {});
  }, [request.patient_preparation]);

  const { mutate: updateServiceRequest, isPending } = useMutation({
    mutationFn: mutate(serviceRequestApi.updateServiceRequest, {
      pathParams: { facilityId, serviceRequestId },
    }),
    onSuccess: () => {
      toast.success(t("patient_preparation_saved"));
      queryClient.invalidateQueries({
        queryKey: ["serviceRequest", facilityId, serviceRequestId],
      });
    },
    onError: () => {
      toast.error(t("patient_preparation_save_failed"));
    },
  });

  const handleSave = () => {
    const updateSpec = toServiceRequestUpdateSpec(request, {
      patient_preparation: preparation,
    });
    updateServiceRequest(updateSpec);
  };

  const updateField = (field: keyof PatientPreparation, value: string) => {
    setPreparation((prev) => ({ ...prev, [field]: value }));
  };

  const hasSomePrepData = Object.values(preparation).some((v) => v && v.trim());
  const isDirty =
    JSON.stringify(preparation) !==
    JSON.stringify(request.patient_preparation ?? {});

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">
          {t("patient_preparation")}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {t("patient_preparation_description")}
        </p>
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Fasting Status */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            {t("fasting_status")}
          </Label>
          <Select
            value={preparation.fasting_status ?? ""}
            onValueChange={(val) => updateField("fasting_status", val)}
            disabled={disableEdit}
          >
            <SelectTrigger id="fasting-status-select" className="w-full">
              <SelectValue placeholder={t("select_fasting_status")} />
            </SelectTrigger>
            <SelectContent>
              {FASTING_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Diet Restrictions */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            {t("diet_restrictions")}
          </Label>
          <Select
            value={preparation.diet_restriction ?? ""}
            onValueChange={(val) => updateField("diet_restriction", val)}
            disabled={disableEdit}
          >
            <SelectTrigger id="diet-restriction-select" className="w-full">
              <SelectValue placeholder={t("select_diet_restriction")} />
            </SelectTrigger>
            <SelectContent>
              {DIET_RESTRICTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bladder Status */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            {t("bladder_status")}
          </Label>
          <Select
            value={preparation.bladder_status ?? ""}
            onValueChange={(val) => updateField("bladder_status", val)}
            disabled={disableEdit}
          >
            <SelectTrigger id="bladder-status-select" className="w-full">
              <SelectValue placeholder={t("select_bladder_status")} />
            </SelectTrigger>
            <SelectContent>
              {BLADDER_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Notes */}
      <div className="px-4 pb-4 space-y-1.5">
        <Label className="text-sm font-medium text-gray-700">
          {t("preparation_notes")}
        </Label>
        <Textarea
          id="preparation-notes"
          value={preparation.notes ?? ""}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder={t("preparation_notes_placeholder")}
          disabled={disableEdit}
          rows={2}
          className="resize-none"
        />
      </div>

      {!disableEdit && (hasSomePrepData || isDirty) && (
        <div className="px-4 pb-4 flex justify-end">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending || !isDirty}
            className="gap-1.5"
          >
            <Save className="size-3.5" />
            {isPending ? t("saving") : t("save_preparation")}
          </Button>
        </div>
      )}
    </div>
  );
}
